import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { createFolder, deleteFolder, updateFolder } from "utils/collegeDocumentService";
import { FILES_QUERY_KEY, FOLDERS_QUERY_KEY, SEARCH_QUERY_KEY } from "./documentLibraryQueryKeys";
import { libraryErrorMessage } from "./libraryErrorMessage";

/**
 * Create/rename and delete mutations for folders. A payload with `folderId` renames (the
 * service defaults `moveRequested` to false, so this never relocates the folder); otherwise a
 * new folder is created under `parentId`. Both invalidate the folder and file lists on success.
 */
export const useFolderMutations = (universityId, { onSaved, onDeleted }) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY, universityId] });
    queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY, universityId] });
    // On-screen search hits name folders and files that just changed, so they go stale too.
    queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY, universityId] });
  };

  const folderMutation = useMutation({
    mutationFn: (payload) =>
      payload.folderId
        ? updateFolder(payload.folderId, { name: payload.name })
        : createFolder({ universityId, parentId: payload.parentId, name: payload.name }),
    onSuccess: () => {
      toast.success("Folder saved");
      invalidate();
      onSaved();
    },
    onError: (error) => toast.error(libraryErrorMessage(error, "Could not save the folder")),
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId) => deleteFolder(folderId),
    onSuccess: () => {
      toast.success("Folder deleted");
      invalidate();
      onDeleted();
    },
    onError: (error) => toast.error(libraryErrorMessage(error, "Could not delete the folder")),
  });

  return { folderMutation, deleteFolderMutation };
};
